import { memo, Suspense, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ForwardToInboxOutlined } from "@mui/icons-material";
import { Box } from "@mui/material";
import { isNil } from "lodash";
import Flx from "../../../../components/layout/Flx";
import LoadingFallback from "../../../../components/LoadingFallback";

import GenericBlockEmailEditor from "../../../../components/Email/GenericBlockEmailEditor";
import GenericEmailEditor from "../../../../components/Email/GenericEmailEditor";
import LoadingContent from "../../../../components/LoadingContent";
import TabContent from "../../../../components/navigation/TabContent";
import ToggleTabSwitcher from "../../../../components/navigation/ToggleTabSwitcher";
import { useLoanTermsHook } from "../../../../hooks/useLoanTermsHook";
import { sidebarSetStateValue } from "../../../../redux/actions/sidebarActions";
import { loanFetchedDataEmailTemplatesSet } from "../../../../redux/reducers/loanFetchedDataReducer";
import TemplateSelector from "../../tabs/LoanDrilldownCommunicationTab/components/TemplateSelector";
import DrilldownSidebarPane from "../DrilldownSidebarPane";

const OBJECT_TYPE_ITEM_LABEL = "Email Composer";

const DrilldownSidebarEmailComposer = memo(() => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const state = useSelector((state) => state.sidebar?.state);

  const templateData = state;
  const [template, setTemplate] = useState(null);

  const template_name = useMemo(() => templateData?.name, [templateData?.name]);

  const title = useMemo(() => {
    return templateData?.template_display_name || OBJECT_TYPE_ITEM_LABEL;
  }, [templateData?.template_display_name]);

  const { previewEmailTemplate } = useLoanTermsHook();

  useEffect(() => {
    if (template_name === "custom") {
      setTemplate({
        template_name: "custom",
        subject: templateData?.subject,
        html_body: templateData?.body,
      });
      return;
    }
    if (isNil(template)) {
      previewEmailTemplate({
        templateName: templateData?.name,
        dealId: loanDrilldown?._id,
        onSuccessFn: (tempData) => {
          setTemplate(tempData || {});
        },
        onFailFn: () => {
          setTemplate("Unable to load template preview");
        },
      });
    } else {
      setTemplate(null);
      previewEmailTemplate({
        templateName: templateData?.name,
        dealId: loanDrilldown?._id,
        onSuccessFn: (tempData) => {
          setTemplate(tempData || {});
        },
        onFailFn: () => {
          setTemplate("Unable to load template preview");
        },
      });
    }
  }, [template_name]);

  return (
    <Suspense fallback={<LoadingFallback />}>
      <DrilldownSidebarPane
        title={title}
        icon={<ForwardToInboxOutlined className="thin" />}
        initialWidth={"600px"}
        bodyPadding={0}
      >
        <Content template={template} />
      </DrilldownSidebarPane>
    </Suspense>
  );
});

const TAB_TYPES = {
  SEND: "Email Composer",
  TEMPLATES: "Templates",
};

const Content = ({ template }) => {
  const [tab, setTab] = useState(TAB_TYPES.SEND);
  const dispatch = useDispatch();
  const { getEmailTemplateNames } = useLoanTermsHook();
  const emailTemplates = useSelector(
    (state) => state.loanFetchedData?.emailTemplates,
  );

  const onTemplateSelect = (template) => {
    if (!template) return;
    setTab(TAB_TYPES.SEND);
    dispatch(sidebarSetStateValue({ ...template }, {}));
  };

  useEffect(() => {
    if (isNil(emailTemplates)) {
      /**
   * Example return
   * {
        "templateNames": [
          "insurance_request",
          "insurance_quote_request",
          "title_request",
          "title_order_request"
          ]
      }
   */

      getEmailTemplateNames({
        onSuccessFn: (d) => {
          const TEMP_NAME_MAP = {
            insurance_request: "Send Homeowners Insurance Request",
            insurance_quote_request: "Send Insurance Quote Request",
            title_request: "Send Title Request",
            title_order_request: "Send Title Order Request",
          };
          const templateList = d?.templateNames || d;
          const convertedToForm = templateList?.map((n) => {
            return {
              template_display_name: TEMP_NAME_MAP[n], // Custom display name for displaying in table of templates
              name: n, // actual template name used for API calls
            };
          });
          // Sets the templates in global state so that they can be display in the template selector.
          // When user clicks/selects a template, "name" is used to fetch the email template
          dispatch(loanFetchedDataEmailTemplatesSet(convertedToForm));
        },
      });
    }
  }, []);

  if (isNil(template)) {
    return <LoadingContent>Loading template...</LoadingContent>;
  }

  return (
    <Flx
      column
      sx={{
        flexGrow: 1,
        height: "100%",
        overflow: "hidden",
        borderTop: "1px solid rgb(224, 221, 213)",
        background: "#fafaf8",
      }}
    >
      <Box
        sx={{
          borderBottom: "1px solid rgb(224, 221, 213)",
          pl: 1.5,
          background: "#ffffff",
        }}
      >
        <ToggleTabSwitcher
          tabs={[TAB_TYPES.SEND, TAB_TYPES.TEMPLATES]}
          value={tab}
          variant={"underline"}
          onChange={setTab}
        />
      </Box>

      <Box
        sx={{
          p: 2,
          pb: 8,
          height: "100%",
          overflowY: "auto",
        }}
      >
        <TabContent activeTab={tab} tabKey={TAB_TYPES.SEND}>
          {template?.template_name === "custom" ? (
            <GenericBlockEmailEditor template={template} />
          ) : (
            <GenericEmailEditor template={template} />
          )}
        </TabContent>
        <TabContent activeTab={tab} tabKey={TAB_TYPES.TEMPLATES}>
          <TemplateSelector
            templates={emailTemplates}
            onSelect={onTemplateSelect}
          />
        </TabContent>
      </Box>
    </Flx>
  );
};


export default DrilldownSidebarEmailComposer;
